// Ship placement rules (suunnitelma.md sections 9–12). Used by the server to validate the
// final fleet and by the UI for immediate feedback.
import {
  BOARD_SIZE,
  FLEET_SIZES,
  inBounds,
  neighbours,
  shipCells,
  type Cell,
  type Orientation,
  type ShipPlacement,
} from './fleet.js';

export type PlacementProblem = 'invalid_ship' | 'out_of_bounds' | 'overlap' | 'touching';
export type FleetProblem = PlacementProblem | 'wrong_fleet';

export type FleetValidation = { ok: true } | { ok: false; error: FleetProblem; shipIndex?: number };

function isValidShipShape(ship: unknown): ship is ShipPlacement {
  const s = ship as ShipPlacement | null;
  return (
    typeof s === 'object' &&
    s !== null &&
    Number.isInteger(s.size) &&
    Number.isInteger(s.x) &&
    Number.isInteger(s.y) &&
    (s.orientation === 'h' || s.orientation === 'v')
  );
}

/** Grid of ship indexes (-1 = empty). */
function occupancy(ships: readonly ShipPlacement[]): number[][] {
  const grid = Array.from({ length: BOARD_SIZE }, () => Array<number>(BOARD_SIZE).fill(-1));
  ships.forEach((ship, index) => {
    for (const c of shipCells(ship)) if (inBounds(c)) grid[c.x]![c.y] = index;
  });
  return grid;
}

/**
 * Checks whether a ship can be added to already placed ships: inside the board,
 * not on another ship and not touching another ship (not even diagonally).
 */
export function checkShip(placed: readonly ShipPlacement[], ship: ShipPlacement): PlacementProblem | null {
  if (!isValidShipShape(ship) || ship.size < 1) return 'invalid_ship';
  const cells = shipCells(ship);
  if (!cells.every(inBounds)) return 'out_of_bounds';
  const grid = occupancy(placed);
  if (cells.some((c) => grid[c.x]![c.y] !== -1)) return 'overlap';
  if (cells.some((c) => neighbours(c).some((n) => grid[n.x]![n.y] !== -1))) return 'touching';
  return null;
}

/** Validates a complete fleet. The server always runs this before accepting a placement. */
export function validateFleet(ships: unknown): FleetValidation {
  if (!Array.isArray(ships) || ships.length !== FLEET_SIZES.length) return { ok: false, error: 'wrong_fleet' };
  for (let i = 0; i < ships.length; i++) {
    if (!isValidShipShape(ships[i])) return { ok: false, error: 'invalid_ship', shipIndex: i };
  }
  const fleet = ships as ShipPlacement[];
  const sizes = fleet.map((s) => s.size).sort((a, b) => b - a);
  if (sizes.some((size, i) => size !== FLEET_SIZES[i])) return { ok: false, error: 'wrong_fleet' };

  for (let i = 0; i < fleet.length; i++) {
    const problem = checkShip(fleet.slice(0, i), fleet[i]!);
    if (problem) return { ok: false, error: problem, shipIndex: i };
  }
  return { ok: true };
}

/** Empty cells where no ship may be placed because they touch a placed ship. */
export function forbiddenCells(placed: readonly ShipPlacement[]): Cell[] {
  const grid = occupancy(placed);
  const seen = new Set<string>();
  const result: Cell[] = [];
  for (const ship of placed) {
    for (const c of shipCells(ship)) {
      for (const n of neighbours(c)) {
        const key = `${n.x},${n.y}`;
        if (grid[n.x]![n.y] === -1 && !seen.has(key)) {
          seen.add(key);
          result.push(n);
        }
      }
    }
  }
  return result;
}

/** All valid positions for a ship of the given size next to already placed ships. */
export function validPositions(placed: readonly ShipPlacement[], size: number): ShipPlacement[] {
  const orientations: Orientation[] = size === 1 ? ['h'] : ['h', 'v'];
  const result: ShipPlacement[] = [];
  for (const orientation of orientations) {
    for (let x = 0; x < BOARD_SIZE; x++) {
      for (let y = 0; y < BOARD_SIZE; y++) {
        const ship = { size, x, y, orientation };
        if (checkShip(placed, ship) === null) result.push(ship);
      }
    }
  }
  return result;
}

/**
 * Generates a random fleet that always obeys the placement rules (suunnitelma.md section 11).
 * Ships are placed largest first at a uniformly chosen valid position; on a dead end the
 * whole fleet is regenerated.
 */
export function randomFleet(random: () => number = Math.random): ShipPlacement[] {
  for (let attempt = 0; attempt < 1000; attempt++) {
    const placed: ShipPlacement[] = [];
    for (const size of FLEET_SIZES) {
      const options = validPositions(placed, size);
      if (options.length === 0) break;
      placed.push(options[Math.floor(random() * options.length)]!);
    }
    if (placed.length === FLEET_SIZES.length) return placed;
  }
  throw new Error('Could not generate a fleet');
}
